/**
 * Start the SPA: read `/config.json`, build the services, mount the router.
 * `main.tsx` calls it with the browser's real ports; everything it touches
 * comes in as a parameter, so it is not tied to a global it cannot see.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserHistory, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ConfigError } from '../components/ConfigError.js';
import { loadRuntimeConfig, type LoadConfig } from '../lib/runtimeConfig.js';
import { ServicesContext } from './hooks.js';
import { connectSession } from './sessionEffects.js';
import { createAppRouter } from './router.js';
import { createServices, type ServiceDeps } from './services.js';

export type StartDeps = Omit<ServiceDeps, 'config'> & {
  readonly container: Element;
  readonly fetchJson: Parameters<LoadConfig>[0];
  /** Register for another tab's storage writes (the browser's `storage` event). */
  readonly onStorageChange: (listener: () => void) => void;
};

/** Query defaults: a failed read retries once (the API client already retried a 401 with a fresh token). */
export function createQueryClient(): QueryClient {
  return new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 5_000, refetchOnWindowFocus: false } } });
}

export async function startApp(deps: StartDeps): Promise<Root> {
  const root = createRoot(deps.container);
  const loaded = await loadRuntimeConfig(deps.fetchJson);
  if (!loaded.ok) {
    root.render(<ConfigError message={loaded.error} />);
    return root;
  }
  const services = createServices({ ...deps, config: loaded.config, shell: loaded.shell });
  const queryClient = createQueryClient();
  const router = createAppRouter({ services, queryClient }, createBrowserHistory());
  connectSession(services.sessions, queryClient, router, deps.onStorageChange);
  services.connectivity.start();
  root.render(
    <StrictMode>
      <ServicesContext.Provider value={services}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ServicesContext.Provider>
    </StrictMode>,
  );
  return root;
}
